/**
 * Centralized Navigation URL Utilities
 * 
 * Hierarchical URL Structure:
 * - Collections: /:collection (e.g., /lumiarc, /lumi-flex)
 * - Families: /:collection/:family (e.g., /lumiarc/adler)
 * - Products: /:collection/:family/:product (e.g., /lumiarc/adler/la9403)
 * 
 * NO query parameters for hierarchical navigation
 */

// Collection slug mapping (URL route → database slug)
const COLLECTION_MAPPING = {
  // URL route → Database collection_slug
  'lumiarc': 'lumi-arc',
  'lumi-flex': 'lumi-flex',
  'lumi-pro': 'lumi-pro',
  'lumi-indoor': 'lumi-indoor',
  'lumi-outdoor': 'lumi-outdoor',
  'lumi-ville': 'lumi-ville',
  'lumi-deco': 'lumi-deco',
  'lumi-brick': 'lumi-brick',
  'lumi-tronix': 'lumi-tronix',
  'lumi-sys': 'lumi-sys',
  'lumi-acoustic': 'lumi-acoustic'
};

// Reverse mapping (database slug → URL route)
const COLLECTION_REVERSE_MAPPING = Object.entries(COLLECTION_MAPPING).reduce((acc, [route, slug]) => {
  acc[slug] = route;
  return acc;
}, {});

/**
 * Get database collection slug from URL route
 */
export function getCollectionSlug(urlRoute) {
  return COLLECTION_MAPPING[urlRoute] || urlRoute;
}

/**
 * Get URL route from database collection slug
 */
export function getCollectionRoute(dbSlug) {
  if (dbSlug === 'lumi-arc' || dbSlug === 'lumiarc') return 'lumi-arc';
  return COLLECTION_REVERSE_MAPPING[dbSlug] || dbSlug;
}

/**
 * Generate clean collection URL
 * @param {string|object} collection - Collection slug or product object
 * @returns {string} Clean collection URL (e.g., /lumiarc)
 */
export function getCollectionUrl(collection) {
  if (!collection) return '/products/catalog';
  
  // If passed a product object
  if (typeof collection === 'object') {
    collection = collection.collection_slug || collection.collection;
  }
  
  // Get URL route from database slug
  const route = getCollectionRoute(collection);
  return `/${route}`;
}

/**
 * Generate clean family URL
 * @param {string|object} collection - Collection slug or product object
 * @param {string} family - Family name (optional if product object passed)
 * @returns {string} Clean family URL (e.g., /lumiarc/adler)
 */
export function getFamilyUrl(collection, family) {
  // Handle product object
  if (typeof collection === 'object') {
    const product = collection;
    family = family || product.family || product.family_name;
    collection = product.collection_slug || product.collection;
  }
  
  if (!collection || !family) return '/products/catalog';
  
  // Get URL route and normalize family
  const collectionRoute = getCollectionRoute(collection);
  const familySlug = family.toLowerCase().replace(/\s+/g, '-');
  
  return `/${collectionRoute}/${familySlug}`;
}

/**
 * Sanitize string for URL slug
 * Removes special characters, replaces spaces with hyphens
 */
function sanitizeForUrl(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .trim()
    // Replace spaces and underscores with hyphens
    .replace(/[\s_]+/g, '-')
    // Remove special characters except hyphens and alphanumeric
    .replace(/[^\w-]+/g, '')
    // Remove multiple consecutive hyphens
    .replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '');
}

/**
 * Generate clean hierarchical product URL
 * @param {object} product - Product object with collection_slug, family, and identifiers
 * @returns {string} Clean product URL (e.g., /lumiarc/adler/la9403)
 */
export function getProductUrl(product) {
  if (!product) return '/products';
  
  // Extract required fields
  const collection = product.collection_slug || product.collection;
  const family = product.family || product.family_name;
  
  // Priority order for clean product identifier:
  // 1. base_code (cleanest, usually just the product code like "LB1548")
  // 2. sku (if available)
  // 3. Extract code from name (as fallback)
  // 4. product_id (last resort)
  let productIdentifier = product.base_code || product.sku;
  
  // If no base_code or sku, try to extract code from name
  if (!productIdentifier && product.name) {
    // Try to extract code pattern like "LB1548" or "LA9403" from name
    const codeMatch = product.name.match(/\b([A-Z]{2,}\d{3,}[A-Z]?)\b/i);
    if (codeMatch) {
      productIdentifier = codeMatch[1];
    } else {
      // Fall back to sanitized name
      productIdentifier = product.name;
    }
  }
  
  // Last resort: use product_id
  if (!productIdentifier) {
    productIdentifier = product.product_id;
  }
  
  // If we have full hierarchy, use clean URLs
  if (collection && family && productIdentifier) {
    const collectionRoute = getCollectionRoute(collection);
    const familySlug = sanitizeForUrl(family);
    const productSlug = String(productIdentifier).toLowerCase().replace(/[^a-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
    
    return `/${collectionRoute}/${familySlug}/${productSlug}`;
  }
  
  // Fallback to legacy format
  return `/products/${sanitizeForUrl(productIdentifier) || product.product_id}`;
}

/**
 * Generate product line URL (legacy - use getCollectionUrl instead)
 * @deprecated Use getCollectionUrl() instead
 */
export function getProductLineUrl() {
  return '/lumiarc';
}

/**
 * Parse hierarchical URL to extract collection, family, and product
 * @param {string} pathname - URL pathname
 * @returns {object} Parsed URL components
 */
export function parseProductUrl(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  
  return {
    collection: segments[0] || null,
    family: segments[1] || null,
    product: segments[2] || null,
    isCollectionPage: segments.length === 1,
    isFamilyPage: segments.length === 2,
    isProductPage: segments.length === 3
  };
}

/**
 * Build breadcrumb items from product data
 * @param {object} product - Product object
 * @returns {array} Breadcrumb items with labels and URLs
 */
export function buildProductBreadcrumbs(product) {
  if (!product) return [];
  
  const crumbs = [
    { label: 'Home', url: '/' },
    { label: 'Products', url: '/products' }
  ];
  
  // Add collection
  if (product.collection_slug || product.collection) {
    const collectionSlug = product.collection_slug || product.collection;
    const collectionName = collectionSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    crumbs.push({
      label: collectionName,
      url: getCollectionUrl(collectionSlug)
    });
  }
  
  // Add family
  if (product.family) {
    crumbs.push({
      label: product.family,
      url: getFamilyUrl(product, product.family)
    });
  }
  
  // Add product (current page, no link)
  if (product.name || product.base_code) {
    crumbs.push({
      label: product.name || product.base_code,
      url: null,
      current: true
    });
  }
  
  return crumbs;
}
